import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface AccessibleBrand {
  id: string;
  name: string;
  accessType: 'owner' | 'participant';
}

export function useUserAccessibleBrands() {
  const { user } = useAuth();
  const [brands, setBrands] = useState<AccessibleBrand[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setBrands([]);
      setIsLoading(false);
      return;
    }

    const fetchBrands = async () => {
      setIsLoading(true);
      try {
        // Fetch brands user owns
        const { data: ownedBrands, error: ownedError } = await supabase
          .from('brands')
          .select('id, name')
          .eq('user_id', user.id);

        if (ownedError) throw ownedError;

        // Fetch brands user participates in via user_brand_access
        const { data: accessBrands, error: accessError } = await supabase
          .from('user_brand_access')
          .select('brand_id, brands:brand_id(id, name)')
          .eq('user_id', user.id);

        if (accessError) throw accessError;

        // Combine and deduplicate
        const brandMap = new Map<string, AccessibleBrand>();

        ownedBrands?.forEach(brand => {
          brandMap.set(brand.id, { id: brand.id, name: brand.name, accessType: 'owner' });
        });

        accessBrands?.forEach(access => {
          const brand = access.brands as { id: string; name: string } | null;
          if (brand && !brandMap.has(brand.id)) {
            brandMap.set(brand.id, { id: brand.id, name: brand.name, accessType: 'participant' });
          }
        });

        setBrands(Array.from(brandMap.values()).sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error('Error fetching accessible brands:', error);
        setBrands([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrands();
  }, [user]);

  return { brands, isLoading };
}
